import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Activity,
  Archive,
  BarChart3,
  Building2,
  CalendarClock,
  ClipboardCheck,
  FileText,
  FileType2,
  FlaskConical,
  GraduationCap,
  HardHat,
  HeartPulse,
  Inbox,
  Boxes,
  Heart,
  Fingerprint,
  ListChecks,
  LogOut,
  Menu,
  ShieldCheck,
  ShieldAlert,
  Settings,
  Stethoscope,
  Trophy,
  UploadCloud,
  UserCheck,
  Users,
  Wrench,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth, roleLabels } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { NotificationsBell } from "@/components/NotificationsBell";
import { apiRequest } from "@/api/client";
import { toast } from "sonner";
import artegoIcon from "@/assets/artego-icon.png";

const logoUrl = artegoIcon;

const navigation = [
  { key: "dashboard", labelDefault: "Dashboard", path: "/", icon: BarChart3, roles: null },
  { key: "companies", labelDefault: "Aziende", path: "/aziende", icon: Building2, roles: null },
  { key: "wizard", labelDefault: "DVR Wizard", path: "/dvr-wizard", icon: ClipboardCheck, roles: null },
  { key: "safety_inbox", labelDefault: "Sopralluoghi guidati", path: "/sopralluoghi-guidati", icon: Inbox, roles: ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP"], badge: "safety" },
  { key: "field_auditors", labelDefault: "Auditor", path: "/auditor-interni", icon: UserCheck, roles: ["SUPER_ADMIN", "CONSULENTE"] },
  { key: "access_log", labelDefault: "Registro accessi", path: "/registro-accessi", icon: Fingerprint, roles: ["SUPER_ADMIN"] },
  { key: "external_audits", labelDefault: "Audit esterni", path: "/audit-esterni", icon: UserCheck, roles: ["SUPER_ADMIN", "CONSULENTE", "RSPP"] },
  { key: "risk_catalog", labelDefault: "Catalogo rischi", path: "/catalogo-rischi", icon: ShieldAlert, roles: ["SUPER_ADMIN", "CONSULENTE"] },
  { key: "tasks", labelDefault: "Mansioni", path: "/mansioni", icon: Users, roles: null },
  { key: "environments", labelDefault: "Ambienti", path: "/ambienti", icon: Boxes, roles: null },
  { key: "risks", labelDefault: "Rischi", path: "/rischi", icon: AlertTriangle, roles: null },
  { key: "equipment", labelDefault: "Attrezzature", path: "/attrezzature", icon: Wrench, roles: null },
  { key: "chemicals", labelDefault: "Sostanze", path: "/sostanze", icon: FlaskConical, roles: null },
  { key: "ppe", labelDefault: "DPI", path: "/dpi", icon: HardHat, roles: null },
  { key: "training", labelDefault: "Formazione", path: "/formazione", icon: GraduationCap, roles: null },
  { key: "health", labelDefault: "Sorveglianza sanitaria", path: "/sorveglianza-sanitaria", icon: HeartPulse, roles: ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP", "MEDICO_COMPETENTE"] },
  { key: "deadlines", labelDefault: "Scadenze", path: "/scadenze", icon: CalendarClock, roles: null },
  { key: "improvement", labelDefault: "Piano miglioramento", path: "/piano-miglioramento", icon: ListChecks, roles: null },
  { key: "documents", labelDefault: "Documenti", path: "/documenti", icon: FileText, roles: null },
  { key: "report", labelDefault: "Report DVR", path: "/report-dvr", icon: ShieldCheck, roles: null },
  { key: "exports", labelDefault: "Esportazioni", path: "/esportazioni", icon: FileType2, roles: null },
  { key: "templates", labelDefault: "Template documenti", path: "/template-documenti", icon: FileText, roles: ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP"] },
  { key: "csv_import", labelDefault: "Import CSV", path: "/import-csv", icon: UploadCloud, roles: ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP"] },
  { key: "admin", labelDefault: "Admin", path: "/admin", icon: Settings, roles: ["SUPER_ADMIN", "CONSULENTE"] },
  { key: "leads_crm", labelDefault: "Lead CRM", path: "/admin/leads", icon: Users, roles: ["SUPER_ADMIN", "CONSULENTE"] },
  { key: "analytics", labelDefault: "Analytics", path: "/analytics", icon: Activity, roles: ["SUPER_ADMIN", "CONSULENTE"] },
  { key: "leaderboard", labelDefault: "Leaderboard", path: "/leaderboard", icon: Trophy, roles: ["SUPER_ADMIN", "CONSULENTE", "RSPP"] },
  { key: "health_alerts", labelDefault: "Health alerts", path: "/health-alerts", icon: Heart, roles: ["SUPER_ADMIN", "CONSULENTE", "RSPP"] },
  { key: "admin_backup", labelDefault: "Backup & cestino", path: "/admin/backup", icon: Archive, roles: ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN"] },
  { key: "audit_logs", labelDefault: "Audit log", path: "/audit-log", icon: ShieldCheck, roles: ["SUPER_ADMIN", "CONSULENTE"] },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [healthAlertCount, setHealthAlertCount] = useState(0);
  const [safetyPending, setSafetyPending] = useState(0);
  const location = useLocation();
  const current = location.pathname.includes("dvr-wizard") ? "DVR Wizard" : location.pathname.includes("moduli-tecnici") ? "Moduli tecnici" : location.pathname.includes("/rischi") ? "Valutazione rischi" : location.pathname.includes("piano-miglioramento") ? "Piano miglioramento" : location.pathname.includes("formazione-dpi-sanitaria") ? "Formazione, DPI, Sanitaria" : navigation.find((item) => item.path === location.pathname)?.labelDefault || "DVR ProSuite";
  const visibleItems = navigation.filter((item) => !item.roles || item.roles.includes(user.role));

  useEffect(() => {
    if (!["SUPER_ADMIN", "CONSULENTE", "RSPP"].includes(user.role)) return;
    apiRequest("/safety/inbox?limit=100").then((r) => setSafetyPending(r.counts?.da_controllare || 0)).catch(() => {});
    apiRequest("/health-alerts")
      .then((r) => setHealthAlertCount(r.alerts_count || 0))
      .catch(() => {});
    const handler = (ev) => {
      const data = ev.detail || {};
      const newIds = data.company_ids || [];
      if (newIds.length > 0) {
        toast.warning(`${newIds.length} nuovo/i alert health score`, {
          description: "Una o più aziende sono scese sotto la soglia 50. Apri Health alerts per dettagli.",
          duration: 8000,
        });
        apiRequest("/health-alerts")
          .then((r) => setHealthAlertCount(r.alerts_count || 0))
          .catch(() => {});
      }
    };
    window.addEventListener("dvr-health_alert", handler);
    return () => window.removeEventListener("dvr-health_alert", handler);
  }, [user.role]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="app-shell">
      <aside className={`fixed inset-y-0 left-0 z-40 w-72 border-r border-slate-800/70 bg-slate-950 text-white transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar-navigation">
        <div className="flex h-full flex-col">
          <div className="flex items-center gap-3 border-b border-white/10 px-5 py-5" data-testid="sidebar-brand">
            <img src={logoUrl} alt="DVR ProSuite" className="h-10 w-10 rounded-md object-contain" data-testid="sidebar-logo" />
            <div>
              <p className="font-heading text-lg font-bold leading-none" data-testid="brand-name">DVR ProSuite</p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-blue-200" data-testid="brand-tagline">Compliance SaaS</p>
            </div>
          </div>
          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" data-testid="sidebar-menu">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  data-testid={`sidebar-nav-${item.key}`}
                  className={({ isActive }) => {
                    const isWizardPath = location.pathname.includes("dvr-wizard");
                    const active = isWizardPath ? item.path === "/dvr-wizard" : isActive;
                    return `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-[background-color,transform,color] duration-200 hover:translate-x-0.5 hover:bg-white/10 ${active ? "bg-blue-700 text-white" : "text-slate-300"}`;
                  }}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  <span>{t(`nav.${item.key}`, item.labelDefault)}</span>
                  {item.key === "safety_inbox" && safetyPending > 0 && (
                    <Badge className="ml-auto border-amber-600 bg-amber-500 text-white" data-testid="sidebar-safety-badge">{safetyPending}</Badge>)}
                  {item.key === "health_alerts" && (
                    <Badge className={`ml-auto ${healthAlertCount > 0 ? "bg-rose-500 text-white border-rose-600" : "bg-slate-700 text-slate-300 border-slate-600"}`} data-testid="sidebar-health-alert-badge">{healthAlertCount}</Badge>
                  )}
                </NavLink>
              );
            })}
          </nav>
          <div className="border-t border-white/10 p-4" data-testid="sidebar-user-panel">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-700 font-bold" data-testid="user-avatar-initials">{user.name?.slice(0, 2).toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user.name}</p>
                <p className="truncate text-xs text-slate-400" data-testid="sidebar-user-email">{user.email}</p>
              </div>
            </div>
            <Button variant="secondary" className="mt-4 w-full bg-white text-slate-950 hover:bg-slate-100" onClick={logout} data-testid="logout-button">
              <LogOut className="h-4 w-4" /> {t("nav.logout")}
            </Button>
            <a href="/legale.html" target="_blank" rel="noopener" className="mt-3 block text-center text-[11px] text-slate-400 transition-colors hover:text-slate-200" data-testid="sidebar-legal-link">Note legali</a>
          </div>
        </div>
      </aside>

      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur" data-testid="topbar">
          <div className="flex min-h-16 items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <Button variant="outline" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
                <Menu className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" data-testid="topbar-section-label">Area operativa</p>
                <h1 className="truncate font-heading text-2xl font-bold text-slate-950" data-testid="topbar-page-title">{current}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <NotificationsBell />
              <Badge className="hidden border-blue-200 bg-blue-50 text-blue-900 sm:inline-flex" data-testid="topbar-user-role">{roleLabels[user.role]}</Badge>
              <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="topbar-system-status"><Stethoscope className="mr-1 h-3 w-3" /> Attivo</Badge>
            </div>
          </div>
        </header>
        <main className="animate-fade-in px-4 py-6 sm:px-6 lg:px-8" data-testid="main-content"><Outlet /></main>
      </div>
      {open && <button className="fixed inset-0 z-30 bg-slate-950/50 lg:hidden" onClick={() => setOpen(false)} data-testid="mobile-sidebar-overlay" aria-label="Chiudi menu" />}
    </div>
  );
}